/**
 * AuthBrandPanel — the left "exhibit" panel shared by the sign-in and sign-up
 * pages. Court-exhibit grammar, matching the homepage (rules not fills, DM Serif
 * headline, DM Mono meta, Outfit body). Clerk-free + presentational so it renders
 * in the harness and can go through the K3 gate like the homepage sections.
 *
 * Orange budget: the panel body is hueless — the only brand color on the auth
 * screen is the wordmark's mark and the form's one primary (Sign in) button. The
 * proof register is carried by rules + hierarchy, never a fill or a hue.
 *
 * Every stat is grounded (verified 2026-10-05):
 *   20    — deterministic engine DAG (homepage ProofBand + architecture)
 *   100+  — live BLS industry indices (bls_live.py: per-NAICS PPI/PCU series)
 *   3     — accountant → attorney → external CPA sign-off chain (TrustLayer)
 *   100%  — every figure carries its engine + source-document lineage
 */

import { FigureCoWordmark } from '../brand/FigureCoLogo';

interface ProofStat {
  stat: string;
  label: string;
}

const PROOF_STATS: ProofStat[] = [
  { stat: '20', label: 'engines in the deterministic calculation DAG' },
  { stat: '100+', label: 'industry indices, live from the Bureau of Labor Statistics' },
  { stat: '3', label: 'roles sign the same number before it exports' },
  { stat: '100%', label: 'of figures traced to a source document' },
];

export default function AuthBrandPanel() {
  return (
    <div className="flex h-full flex-col justify-between border-r border-hairline p-12">
      {/* Wordmark */}
      <FigureCoWordmark size="lg" />

      {/* Spine */}
      <div className="max-w-xl">
        <h1 className="font-heading text-4xl leading-[1.08] tracking-tight text-text-primary">
          The number that holds up in court.
        </h1>
        <p className="mt-5 max-w-md font-body text-base leading-relaxed text-text-secondary">
          Deterministic engines compute every figure. The right human signs it.
          Every number traces to the document it came from.
        </p>

        {/* Proof register — ruled, hueless */}
        <dl className="mt-10 border-t border-hairline">
          {PROOF_STATS.map((s) => (
            <div
              key={s.label}
              className="grid grid-cols-[4.5rem_1fr] items-baseline gap-5 border-b border-hairline py-4"
            >
              <dt className="font-mono text-2xl leading-none tracking-tight text-text-primary tabular-nums">
                {s.stat}
              </dt>
              <dd className="font-body text-sm leading-snug text-text-secondary">{s.label}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Footer */}
      <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">
        © {new Date().getFullYear()} The Figure Co. · figures attested by your own licensed professionals
      </p>
    </div>
  );
}
